import { EAgentLandingPageType } from './agent-landing-page';
import { ELeadResponseType } from './lead-response';

export enum EAdCampaignStatus {
  PendingBilling = 'PENDING_BILLING',
  PendingMarketingProfile = 'PENDING_MARKETING_PROFILE',
  PendingAdApproval = 'PENDING_AD_APPROVAL',
  Active = 'ACTIVE',
  Paused = 'PAUSED',
}

export enum EAdCreativeSource {
  HarvistGenerated = 'HARVIST_GENERATED',
  UserUpload = 'USER_UPLOAD',
}

export enum EAdCreativeMediaType {
  Image = 'IMAGE',
  Video = 'VIDEO',
}

export enum EAdCreativeAssetStatus {
  PendingUpload = 'PENDING_UPLOAD',
  Processing = 'PROCESSING',
  Ready = 'READY',
  Attached = 'ATTACHED',
  Failed = 'FAILED',
  Expired = 'EXPIRED',
}

export interface IAdCampaign {
  _id: string;
  user: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  name: string;
  callToActionLink: string;
  useCustomCallToActionLink: boolean;
  agentLandingPageType?: EAgentLandingPageType;
  zipCodes: string[];
  customAudienceCampaigns: {
    _id: string;
    name: string;
    numSubscribedApns: number;
  }[];
  costs: {
    userMonthlyBudget: number; // User's cost for this ad campaign
    nextInvoiceDate?: Date;
  };
  paymentMethodId?: string;
  latestImagePreviewUrl?: string;
  creative: {
    // Whether Harvist rendered the creative or the user supplied it.
    source: EAdCreativeSource;
    mediaType: EAdCreativeMediaType;

    // URL resolved by the backend from the uploaded asset or the latest
    // Harvist-generated creative version.
    previewUrl?: string;

    // Generated still image displayed before a video plays. Images do not
    // return this field.
    videoThumbnailUrl?: string;
  };
  status: EAdCampaignStatus;
  createdAt: Date;

  stats: IAdCampaignStats;
}

export interface IAdCampaignLead {
  _id: string;
  type: ELeadResponseType;
  date: Date;

  fullName?: string;
  phoneNumber?: string;
  email?: string;
  address?: string;
  message?: string;
  zipCode?: string;
  propertyAddress?: string;
  requestType?: string;
  sellingTimeline?: string;
  reportContext?: string;
}

export interface IAdCampaignStats {
  reach?: number;
  impressions?: number;
  inlineLinkClicks?: number;
}

export interface CreateAdCampaignDto {
  name: string;
  useCustomCallToActionLink: boolean;
  customCallToActionLink: string;
  budget: number;
  customAudienceCampaignIds: string[];
  zipCodes: string[];
  paymentMethodId: string;
  creative:
  | {
    source: EAdCreativeSource.HarvistGenerated;
  }
  | {
    source: EAdCreativeSource.UserUpload;
    assetId: string;
  };
}

export interface CreateAdCampaignInput extends Omit<CreateAdCampaignDto, 'creative'> {
  creative:
  | {
    source: EAdCreativeSource.HarvistGenerated;
  }
  | {
    source: EAdCreativeSource.UserUpload;
    mediaType: EAdCreativeMediaType;
    file: File;
  };
}

export interface IAdCreativeAsset {
  _id: string;

  // Server-side upload/validation/Meta processing state polled by the client.
  status: EAdCreativeAssetStatus;
  mediaType: EAdCreativeMediaType;

  // Metadata verified by the backend against the staged S3 object.
  mimeType: string;
  byteSize: number;
  width?: number;
  height?: number;

  // Video-only metadata populated after ffprobe validation.
  durationSeconds?: number;
  framesPerSecond?: number;

  // URL of the primary uploaded image or playable video.
  previewUrl?: string;

  // Video-only still image used before playback and as Meta's thumbnail.
  videoThumbnailUrl?: string;

  // Expected expiration of the lifecycle-managed tmp object. The backend
  // rejects attachment after this point; S3 performs the actual deletion.
  stagingExpiresAt?: Date;
  errorMessage?: string;
}

export interface UpdateAdCampaignDto {
  name?: string;
  status?: EAdCampaignStatus.Paused | EAdCampaignStatus.Active;
  paymentMethodId?: string;
}
